import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { formatInstant } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import {
  GPS_PROVIDERS,
  GPS_PROVIDER_LABELS,
  SAMSARA_REGIONS,
  isConnected,
  usesLogin,
  parseGpsConnections,
  parseGpsMatchReport,
  unitLabel,
  type GpsConnection,
  type GpsProvider,
} from '../../lib/gps-connections';

/**
 * Settings → AI & integrations → GPS Connectors (API 0440, Geotab 0442).
 *
 * One card per provider, Motive, Samsara and Geotab, whether or not the tenant has a
 * row: no row and a `disconnected` row both read "Not connected". A connected
 * card carries its match report underneath — the three lists the API's
 * matcher produced, rendered as returned. Counts are the API's
 * (`vehicles_matched` from the last sync, the report's own arrays); nothing
 * here re-matches a vehicle.
 *
 * Every route is requireRole('admin', strict), so an `owner` (whom the tab's
 * adminOnly flag admits) reads a refusal here, not two empty cards.
 *
 * The key is write-only. The API stores it and returns key_last4; this page
 * never holds the key past the modal's own input. Geotab logs in with a
 * username, password and database instead: its card shows the username and
 * database, and no part of the password exists anywhere after the modal closes.
 */

export const GPS_CONNECTIONS_QUERY_KEY = ['settings', 'gps-connections'];
const matchReportKey = (provider: GpsProvider) => [...GPS_CONNECTIONS_QUERY_KEY, provider, 'match-report'];

const BTN = 'px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50';
const BTN_SECONDARY = `${BTN} bg-fieldiq-card border border-fieldiq-border text-fieldiq-text hover:border-fieldiq-accent`;
const BTN_DANGER = `${BTN} bg-fieldiq-card border border-fieldiq-border text-fieldiq-text hover:border-fieldiq-danger`;
const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-text text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

function apiError(err: any, fallback: string): string {
  return err?.response?.data?.error || fallback;
}

/** status → dot + words. An unrecognised status claims nothing: slate and an em dash. */
function statusDisplay(c: GpsConnection | undefined): { dot: string; label: string } {
  if (!isConnected(c)) return { dot: 'bg-fi-signal-slate', label: 'Not connected' };
  if (c.status === 'active') return { dot: 'bg-fi-signal-green', label: 'Connected' };
  if (c.status === 'error') return { dot: 'bg-fi-signal-red', label: 'Sync error' };
  return { dot: 'bg-fi-signal-slate', label: '—' };
}

function regionLabel(region: string | null): string | null {
  return SAMSARA_REGIONS.find((r) => r.value === region)?.label ?? region;
}

function ConnectDialog({
  provider,
  onClose,
}: {
  provider: GpsProvider;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState('');
  const [region, setRegion] = useState<string>('us');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [database, setDatabase] = useState('');
  const [error, setError] = useState<string | null>(null);
  const label = GPS_PROVIDER_LABELS[provider];
  const login = usesLogin(provider);

  // The password is sent as typed, never trimmed — the API does not trim it
  // either, because a password may legitimately end in a space.
  const ready = login
    ? username.trim() !== '' && password !== '' && database.trim() !== ''
    : apiKey.trim() !== '';

  const connect = useMutation({
    mutationFn: () =>
      api.gpsConnections.connect(
        provider === 'geotab'
          ? { provider, username: username.trim(), password, database: database.trim() }
          : provider === 'samsara'
            ? { provider, api_key: apiKey.trim(), region }
            : { provider, api_key: apiKey.trim() },
      ),
    onSuccess: () => {
      toast.success(`${label} connected`);
      onClose();
    },
    // The API tests the key against the provider before storing anything, so a
    // refusal here means nothing was saved. Its message stays in the modal.
    onError: (err: any) => setError(apiError(err, `Could not connect ${label}`)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: GPS_CONNECTIONS_QUERY_KEY }),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !connect.isPending) onClose(); }}>
      <DialogContent>
        <form
          role="dialog"
          aria-modal="true"
          aria-label={`Connect ${label}`}
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            connect.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Connect {label}</DialogTitle>
            <p className="mt-1 text-xs text-fieldiq-muted">
              {login
                ? `FieldIQ signs in to ${label} before saving the login. The password is never shown afterwards.`
                : `FieldIQ checks the key with ${label} before saving it. Only its last four characters are shown afterwards.`}
            </p>
          </DialogHeader>
          <div className="space-y-4">
            {login ? (
              <>
                <p className="text-xs text-fieldiq-muted" data-testid="gps-geotab-help">
                  Use a View only service account. Database name is the part after my.geotab.com/ in your MyGeotab address.
                </p>
                <div>
                  <label htmlFor="gps-username" className="mb-1 block text-sm font-medium text-fieldiq-text">Username</label>
                  <input
                    id="gps-username"
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    className={inputCls}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="gps-password" className="mb-1 block text-sm font-medium text-fieldiq-text">Password</label>
                  <input
                    id="gps-password"
                    type="password"
                    autoComplete="new-password"
                    spellCheck={false}
                    className={inputCls}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="gps-database" className="mb-1 block text-sm font-medium text-fieldiq-text">Database name</label>
                  <input
                    id="gps-database"
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    className={inputCls}
                    value={database}
                    onChange={(e) => setDatabase(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div>
                <label htmlFor="gps-api-key" className="mb-1 block text-sm font-medium text-fieldiq-text">API key</label>
                <input
                  id="gps-api-key"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  className={`${inputCls} font-mono`}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={`Paste your ${label} API key`}
                />
              </div>
            )}
            {provider === 'samsara' && (
              <div>
                <label htmlFor="gps-region" className="mb-1 block text-sm font-medium text-fieldiq-text">Region</label>
                <select id="gps-region" className={inputCls} value={region} onChange={(e) => setRegion(e.target.value)}>
                  {SAMSARA_REGIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-fieldiq-muted">A Samsara key only works in the region its account is in.</p>
              </div>
            )}
            {error && (
              <p role="alert" data-testid="gps-connect-error" className="rounded-lg border border-fieldiq-danger/40 bg-fieldiq-danger/10 px-3 py-2 text-sm text-fieldiq-text">
                {error}
              </p>
            )}
          </div>
          <DialogFooter>
            <button type="button" className={BTN_SECONDARY} onClick={onClose} disabled={connect.isPending}>
              Cancel
            </button>
            <button
              type="submit"
              className="fi-action fi-action--compact fi-action--primary disabled:opacity-50"
              disabled={connect.isPending || !ready}
            >
              {connect.isPending && <Loader2 size={12} className="animate-spin" />}
              Save
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MatchReport({ provider }: { provider: GpsProvider }) {
  const { data, isLoading, error } = useQuery({
    queryKey: matchReportKey(provider),
    queryFn: () => api.gpsConnections.matchReport(provider).then((r) => parseGpsMatchReport(unwrap(r.data))),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const label = GPS_PROVIDER_LABELS[provider];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-5 py-4 text-xs text-fieldiq-muted">
        <Loader2 size={12} className="animate-spin" /> Loading match report…
      </div>
    );
  }
  if (error || !data) {
    return <LoadFailureNotice error={error ?? new Error('empty')} subject={`the ${label} match report`} allowed="admins" className="py-6" />;
  }

  const section = 'space-y-2';
  const heading = 'text-xs font-semibold uppercase tracking-wide text-fieldiq-muted';
  const list = 'divide-y divide-fieldiq-border rounded-lg border border-fieldiq-border';
  const item = 'px-3 py-2 text-sm text-fieldiq-text flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1';

  return (
    <div className="space-y-5 px-5 py-4" data-testid={`gps-match-report-${provider}`}>
      <section className={section} aria-label={`Matched units (${data.matched.length})`}>
        <h3 className={heading}>Matched units ({data.matched.length})</h3>
        {data.matched.length === 0 ? (
          <p className="text-xs text-fieldiq-muted">No {label} vehicle matches a FieldIQ vehicle yet.</p>
        ) : (
          <ul className={list}>
            {data.matched.map((m) => (
              <li key={`${m.equipmentId}-${m.providerVehicleId}`} className={item}>
                <span>{unitLabel(m)}</span>
                <span className="text-xs text-fieldiq-muted">
                  {m.providerVehicleName ?? '—'}
                  {m.matchedOn ? ` · by ${m.matchedOn === 'vin' ? 'VIN' : m.matchedOn === 'plate' ? 'plate' : m.matchedOn}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={section} aria-label={`${label} vehicles not matched (${data.unmatchedProviderVehicles.length})`}>
        <h3 className={heading}>{label} vehicles not matched ({data.unmatchedProviderVehicles.length})</h3>
        {data.unmatchedProviderVehicles.length === 0 ? (
          <p className="text-xs text-fieldiq-muted">Every {label} vehicle is matched.</p>
        ) : (
          <ul className={list}>
            {data.unmatchedProviderVehicles.map((v) => (
              <li key={v.providerVehicleId} className={item}>
                <span>{v.name ?? '—'}</span>
                <span className="text-xs text-fieldiq-muted">
                  VIN {v.vin ?? '—'} · Plate {v.plate ?? '—'}
                  {v.reason === 'ambiguous' ? ' · more than one FieldIQ vehicle fits' : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={section} aria-label={`FieldIQ vehicles missing VIN and plate (${data.missingIdentifiers.length})`}>
        <h3 className={heading}>FieldIQ vehicles missing VIN and plate ({data.missingIdentifiers.length})</h3>
        {data.missingIdentifiers.length === 0 ? (
          <p className="text-xs text-fieldiq-muted">Every FieldIQ vehicle has a VIN or a plate.</p>
        ) : (
          <>
            <p className="text-xs text-fieldiq-muted">Add a VIN or plate to match.</p>
            <ul className={list}>
              {data.missingIdentifiers.map((e) => (
                <li key={e.equipmentId} className={item}>
                  <span>{unitLabel(e)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

/** A term and its value, the value named by its term. */
function Field({ id, label, mono, children }: { id: string; label: string; mono?: boolean; children: React.ReactNode }) {
  return (
    <>
      <dt id={`gps-${id}`} className="whitespace-nowrap text-fieldiq-muted">{label}</dt>
      <dd aria-labelledby={`gps-${id}`} className={`text-fieldiq-text ${mono ? 'font-mono' : ''}`}>{children}</dd>
    </>
  );
}

function ProviderCard({
  provider,
  connection,
  onConnect,
  onDisconnect,
}: {
  provider: GpsProvider;
  connection: GpsConnection | undefined;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const label = GPS_PROVIDER_LABELS[provider];
  const connected = isConnected(connection);
  const { dot, label: statusLabel } = statusDisplay(connection);

  const sync = useMutation({
    mutationFn: () => api.gpsConnections.sync(provider),
    onSuccess: () => toast.success(`${label} synced`),
    // A failed sync stamps last_error on the row; the refetch below shows it.
    onError: (err: any) => toast.error(apiError(err, `${label} sync failed`)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: GPS_CONNECTIONS_QUERY_KEY }),
  });

  const lastSync = connected ? formatInstant(connection.lastSyncAt, timeZone, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }) : null;

  return (
    <section
      data-testid={`gps-card-${provider}`}
      aria-label={label}
      className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      {/* Name and status on one line, the buttons on a row of their own
          beneath, so no button can sit over the status at any width. */}
      <div className="space-y-3 p-5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h2 className="text-base font-semibold text-fieldiq-text">{label}</h2>
          <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 rounded-full ${dot}`} />
          <span className="whitespace-nowrap text-xs text-fieldiq-muted" data-testid={`gps-status-${provider}`}>{statusLabel}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {connected ? (
            <>
              <button type="button" className={`${BTN_SECONDARY} inline-flex items-center gap-1.5`} disabled={sync.isPending} onClick={() => sync.mutate()}>
                {sync.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Sync now
              </button>
              <button type="button" className={BTN_SECONDARY} onClick={onConnect}>
                {usesLogin(provider) ? 'Replace login' : 'Replace key'}
              </button>
              <button type="button" className={BTN_DANGER} onClick={onDisconnect}>Disconnect</button>
            </>
          ) : (
            <button type="button" className="fi-action fi-action--compact fi-action--primary" onClick={onConnect}>
              Connect
            </button>
          )}
        </div>
        <div className="min-w-0 space-y-1">
          {connected ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
              {usesLogin(provider) ? (
                <>
                  <Field id={`${provider}-username`} label="Username">{connection.accountUsername ?? '\u2014'}</Field>
                  <Field id={`${provider}-database`} label="Database">{connection.accountDatabase ?? '\u2014'}</Field>
                </>
              ) : (
                <Field id={`${provider}-key`} label="API key" mono>
                  {connection.keyLast4 ? `••••${connection.keyLast4}` : '\u2014'}
                </Field>
              )}
              {provider === 'samsara' && (
                <Field id={`${provider}-region`} label="Region">{regionLabel(connection.region) ?? '\u2014'}</Field>
              )}
              <Field id={`${provider}-last-sync`} label="Last sync">{lastSync ?? '\u2014'}</Field>
              <Field id={`${provider}-matched`} label="Vehicles matched">{connection.vehiclesMatched ?? '\u2014'}</Field>
            </dl>
          ) : (
            <p className="text-xs text-fieldiq-muted">Odometer and engine hours for your vehicles, synced every 4 hours from {label}.</p>
          )}
          {connected && connection.lastError && (
            <p role="alert" className="mt-2 rounded-lg border border-fieldiq-danger/40 bg-fieldiq-danger/10 px-3 py-2 text-xs text-fieldiq-text">
              <span className="font-semibold">Last error:</span> {connection.lastError}
            </p>
          )}
        </div>
      </div>
      {connected && (
        <div className="border-t border-fieldiq-border">
          <MatchReport provider={provider} />
        </div>
      )}
    </section>
  );
}

export default function GpsConnectorsPage() {
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState<GpsProvider | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState<GpsProvider | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: GPS_CONNECTIONS_QUERY_KEY,
    queryFn: () => api.gpsConnections.list().then((r) => parseGpsConnections(unwrap(r.data))),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const disconnect = useMutation({
    mutationFn: (provider: GpsProvider) => api.gpsConnections.disconnect(provider),
    onSuccess: (_res, provider) => {
      toast.success(`${GPS_PROVIDER_LABELS[provider]} disconnected`);
      setConfirmDisconnect(null);
    },
    onError: (err: any) => toast.error(apiError(err, 'Failed to disconnect')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: GPS_CONNECTIONS_QUERY_KEY }),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-fieldiq-accent" /></div>;
  }

  const byProvider = new Map((data ?? []).map((c) => [c.provider, c]));

  return (
    <div data-testid="gps-connectors-page" className="min-h-full">
      {/* The AI & integrations workspace renders the title and the tab strip. */}
      <div className="space-y-4 px-6 py-4 pb-[var(--bottom-chrome)]">
        <p className="text-xs text-fieldiq-muted">
          Connect a telematics provider to read odometer and engine hours for your vehicles. Vehicles match on VIN, then plate.
        </p>
        {error ? (
          <LoadFailureNotice error={error} subject="GPS connections" allowed="admins" />
        ) : (
          <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
            {GPS_PROVIDERS.map((p) => (
              <ProviderCard
                key={p}
                provider={p}
                connection={byProvider.get(p)}
                onConnect={() => setConnecting(p)}
                onDisconnect={() => setConfirmDisconnect(p)}
              />
            ))}
          </div>
        )}
      </div>

      {connecting && <ConnectDialog provider={connecting} onClose={() => setConnecting(null)} />}

      <ConfirmModal
        isOpen={confirmDisconnect !== null}
        title={confirmDisconnect ? `Disconnect ${GPS_PROVIDER_LABELS[confirmDisconnect]}?` : ''}
        body={`The stored ${confirmDisconnect && usesLogin(confirmDisconnect) ? 'password' : 'API key'} is deleted and scheduled syncs stop. Readings already recorded stay on each unit.`}
        confirmLabel="Disconnect"
        loading={disconnect.isPending}
        onConfirm={() => confirmDisconnect && disconnect.mutate(confirmDisconnect)}
        onCancel={() => setConfirmDisconnect(null)}
      />
    </div>
  );
}
